import { keyframes } from '@emotion/react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { GradientSpinner } from '../../../core/components/Loading';

const FONT_TIME = "'Barlow Condensed', sans-serif";
const FONT_BODY = "'Zen Kaku Gothic New', sans-serif";

const cardReveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const timeStyle = {
  fontFamily: FONT_TIME,
  fontSize: '1.2rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
  lineHeight: 1.15,
  background: 'linear-gradient(135deg, #d4a0ff 0%, #FFD700 50%, #ffeaa0 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  filter: 'drop-shadow(0 0 10px rgba(255,215,0,0.15))',
};

const timeLabelStyle = {
  fontFamily: FONT_BODY,
  fontSize: '0.5rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.25)',
  letterSpacing: '0.06em',
  minWidth: 10,
  textAlign: 'right',
};

export function CertificateCard({ certificate, onClickCard, index = 0 }) {
  const { thumbnailURL, eventDate, eventName, time, netTime } = certificate;
  const dateStr = eventDate?.toDate?.()
    ? eventDate.toDate().toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric' })
    : '';

  return (
    <Card
      elevation={0}
      sx={{
        position: 'relative',
        aspectRatio: '3 / 4',
        overflow: 'hidden',
        cursor: thumbnailURL ? 'pointer' : 'default',
        border: '1px solid rgba(255,255,255,0.05)',
        animation: `${cardReveal} 0.6s cubic-bezier(0.16, 1, 0.3, 1) both`,
        animationDelay: `${index * 0.1}s`,
        transition: 'border-color 0.35s, box-shadow 0.35s, transform 0.35s',
        transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, #8a2be2, #FFD700)',
          opacity: 0,
          transition: 'opacity 0.35s ease',
          zIndex: 5,
          borderRadius: 'inherit',
        },
        '&:hover::before': { opacity: 1 },
        '&:hover': {
          borderColor: 'rgba(138,43,226,0.25)',
          boxShadow: '0 4px 16px rgba(138,43,226,0.08), 0 16px 40px rgba(0,0,0,0.3)',
          transform: 'translateY(-4px)',
        },
      }}
      onClick={() => thumbnailURL && onClickCard(certificate)}
    >
      {thumbnailURL ? (
        <Box
          component="img"
          src={thumbnailURL}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'top center',
            transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
            '.MuiCard-root:hover &': { transform: 'scale(1.06)' },
          }}
        />
      ) : (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'background.paper',
          }}
        >
          <GradientSpinner size={32} />
          <Typography
            sx={{
              fontFamily: FONT_BODY,
              fontSize: '0.6rem',
              color: 'rgba(255,255,255,0.2)',
              letterSpacing: '0.08em',
              mt: 1,
            }}
          >
            処理中...
          </Typography>
        </Box>
      )}

      {/* Dark scrim */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to top, rgba(6,6,12,0.95) 0%, rgba(6,6,12,0.75) 35%, rgba(6,6,12,0.2) 60%, rgba(6,6,12,0.05) 100%)',
          zIndex: 1,
        }}
      />

      {/* Content overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          p: 1.75,
          zIndex: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'rgba(255,255,255,0.7)',
            letterSpacing: '0.1em',
            textShadow: '0 1px 4px rgba(0,0,0,0.5)',
          }}
        >
          {dateStr}
        </Typography>

        <Typography
          noWrap
          sx={{
            fontFamily: FONT_BODY,
            fontSize: '0.85rem',
            fontWeight: 700,
            color: '#fff',
            lineHeight: 1.3,
            mt: 0.25,
            textShadow: '0 1px 6px rgba(0,0,0,0.4)',
          }}
        >
          {eventName}
        </Typography>

        {/* Times */}
        <Box sx={{ mt: 1 }}>
          {[
            { label: 'G', value: time },
            { label: 'N', value: netTime },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography sx={timeLabelStyle}>{label}</Typography>
              <Typography sx={timeStyle}>{value}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Card>
  );
}
